import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  getBackupLogs,
  getBackupSettings,
  getBackupSnapshots,
  restoreBackupSnapshot,
  runBackupNow,
  updateBackupSettings,
} from '../lib/api';

const FREQUENCIES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const fmt = (v) => (v ? new Date(v).toLocaleString() : '-');

export function SettingsBackupTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [settings, setSettings] = useState({ enabled: true, frequency: 'weekly', retention_count: 20, last_success_at: null, next_due_at: null });
  const [logs, setLogs] = useState([]);
  const [snapshots, setSnapshots] = useState([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, lRes, snapRes] = await Promise.all([
        getBackupSettings(),
        getBackupLogs({ limit: 50 }),
        getBackupSnapshots({ limit: 100 }),
      ]);
      setSettings(sRes.data);
      setLogs(lRes.data.rows || []);
      setSnapshots(snapRes.data.rows || []);
    } catch (e) {
      toast.error('Failed to load backup settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await updateBackupSettings({
        enabled: !!settings.enabled,
        frequency: settings.frequency,
        retention_count: Math.max(1, Number(settings.retention_count || 20)),
      });
      setSettings(res.data);
      toast.success('Backup settings saved');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save backup settings');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await runBackupNow();
      toast.success('Backup completed');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backup failed');
    } finally {
      setRunning(false);
    }
  };

  const restore = async (snapshotId) => {
    const confirm = window.prompt(`Type RESTORE to restore snapshot ${snapshotId}. This replaces current database state.`);
    if (!confirm) return;
    setRestoring(true);
    try {
      await restoreBackupSnapshot(snapshotId, { confirm_text: confirm });
      toast.success(`Snapshot ${snapshotId} restored`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50/40 p-4 sm:p-6">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
        <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
          <h2 className="text-lg font-semibold text-gray-900">Backups</h2>
          <p className="mt-1 text-sm text-gray-500">Configure automatic backup frequency and verify execution logs.</p>

          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <label className="text-xs text-gray-600">
              Enabled
              <select className="mt-1 w-full rounded border px-2 py-1.5 text-sm" value={settings.enabled ? 'yes' : 'no'} onChange={(e) => setSettings((s) => ({ ...s, enabled: e.target.value === 'yes' }))}>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label className="text-xs text-gray-600">
              Frequency
              <select className="mt-1 w-full rounded border px-2 py-1.5 text-sm" value={settings.frequency} onChange={(e) => setSettings((s) => ({ ...s, frequency: e.target.value }))}>
                {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-600">
              Retention count
              <input className="mt-1 w-full rounded border px-2 py-1.5 text-sm" type="number" min={1} step={1} value={settings.retention_count} onChange={(e) => setSettings((s) => ({ ...s, retention_count: e.target.value }))} />
            </label>
            <div className="flex items-end gap-2">
              <button type="button" className="rounded bg-gray-900 px-3 py-2 text-xs font-medium text-white disabled:opacity-50" onClick={saveSettings} disabled={saving || loading}>{saving ? 'Saving…' : 'Save settings'}</button>
              <button type="button" className="rounded border border-gray-300 px-3 py-2 text-xs font-medium text-gray-800 disabled:opacity-50" onClick={runNow} disabled={running || loading}>{running ? 'Running…' : 'Run backup now'}</button>
            </div>
          </div>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <p className="text-xs text-gray-600">Last successful backup: <span className="font-mono">{fmt(settings.last_success_at)}</span></p>
            <p className="text-xs text-gray-600">Next due backup: <span className="font-mono">{fmt(settings.next_due_at)}</span></p>
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-900">Execution logs</h3>
          <div className="mt-3 overflow-x-auto rounded border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Started</th>
                  <th className="px-3 py-2 text-left">Finished</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Trigger</th>
                  <th className="px-3 py-2 text-left">Snapshot</th>
                  <th className="px-3 py-2 text-left">Message</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={6}>No backup logs yet.</td></tr>
                ) : logs.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-2 text-xs">{fmt(r.started_at)}</td>
                    <td className="px-3 py-2 text-xs">{fmt(r.finished_at)}</td>
                    <td className="px-3 py-2 text-xs">{r.status}</td>
                    <td className="px-3 py-2 text-xs">{r.trigger}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.snapshot_id || '-'}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.message || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-xl border border-red-200 bg-white p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-red-800">Restore snapshot (destructive)</h3>
          <p className="mt-1 text-xs text-red-700">Restoring replaces current database collections with snapshot files.</p>
          <div className="mt-3 overflow-x-auto rounded border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Snapshot</th>
                  <th className="px-3 py-2 text-left">Created</th>
                  <th className="px-3 py-2 text-left">Files</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.length === 0 ? (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={4}>No snapshots available.</td></tr>
                ) : snapshots.map((s) => (
                  <tr key={s.snapshot_id} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{s.snapshot_id}</td>
                    <td className="px-3 py-2 text-xs">{fmt(s.created_at)}</td>
                    <td className="px-3 py-2 text-xs">{s.file_count}</td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 disabled:opacity-50" disabled={restoring || loading} onClick={() => restore(s.snapshot_id)}>
                        Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
